import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Pause as HoldIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Warning as WarningIcon,
  People as PeopleIcon,
  Event as EventIcon
} from '@mui/icons-material';

const PendingAccountRemarks = ({ 
  open, 
  onClose, 
  account, 
  actionType, // 'AP', 'RJ', 'HO'
  onVerification 
}) => {
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [totalUserSlots, setTotalUserSlots] = useState('');
  const [expiryDate, setExpiryDate] = useState(null);

  const MAX_REMARKS_LENGTH = 500;

  useEffect(() => {
    if (open) {
      setRemarks('');
      setError('');
      setCharCount(0);
      setShowConfirmation(false);
      setTotalUserSlots('');
    }
  }, [open]);

  if (!account) return null;

  const getActionConfig = (actionType) => {
    console.log('actionType ', actionType);
    switch (actionType) {
      case 'AP':
        return {
          title: 'Approve Account',
          buttonText: 'Approve',
          buttonColor: 'success',
          buttonIcon: <ApproveIcon />,
          status: 'APPROVED'
        };
      case 'RJ':
        return {
          title: 'Reject Account',
          buttonText: 'Reject',
          buttonColor: 'error',
          buttonIcon: <RejectIcon />,
          status: 'REJECTED'
        };
      case 'HO':
        return {
          title: 'Hold Account',
          buttonText: 'Hold',
          buttonColor: 'warning',
          buttonIcon: <HoldIcon />,
          status: 'HOLD'
        };
      default:
        return {
          title: 'Account Verification',
          buttonText: 'Submit',
          buttonColor: 'primary',
          buttonIcon: null,
          status: ''
        };
    }
  };

  const actionConfig = getActionConfig(actionType);

  const validateFields = () => {
    const errors = [];

    // Validate total user slots if provided
    if (totalUserSlots && (isNaN(totalUserSlots) || parseInt(totalUserSlots) < 0)) {
      errors.push('Total user slots must be a positive number.');
    }

    // Validate expiry date if provided
    if (expiryDate) {
      const today = dayjs().startOf('day');
      const maxDate = dayjs().add(30, 'day');

      if (expiryDate.isBefore(today)) {
        errors.push('Expiry date cannot be in the past.');
      } else if (expiryDate.isAfter(maxDate)) {
        errors.push('Expiry date cannot be more than 30 days in the future.');
      }
    }

    return errors;
  };

  const handleSubmit = async () => {
    if (!remarks.trim()) {
      setError('Please provide remarks for this action.');
      return;
    }

    if (remarks.trim().length < 10) {
      setError('Remarks must be at least 10 characters long.');
      return;
    }

    // Validate custom fields
    const validationErrors = validateFields();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(' '));
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Prepare custom data
      const customData = {};
      
      if (totalUserSlots && totalUserSlots.trim() !== '') {
        customData.total_user_slots = parseInt(totalUserSlots);
      }
      if (expiryDate) {
        // Format date as YYYY-MM-DD
        const formattedDate = expiryDate.format('YYYY-MM-DD');
        customData.expiry_date = formattedDate;
      }

      console.log('Custom data being sent:', customData);
      await onVerification(account.id, remarks.trim(), actionType, customData);
      setShowConfirmation(true);
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      setError(err.message || 'An error occurred while processing the verification.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setRemarks('');
    setError('');
    setCharCount(0);
    setLoading(false);
    setShowConfirmation(false);
    setTotalUserSlots('');
    setExpiryDate(null);
    onClose();
  };

  const handleRemarksChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_REMARKS_LENGTH) {
      setRemarks(value);
      setCharCount(value.length);
      setError('');
    }
  };
  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
      disableBackdropClick={loading}
    >
      <DialogTitle sx={{ 
        bgcolor: actionConfig.buttonColor === 'success' ? 'success.light' : 
                 actionConfig.buttonColor === 'error' ? 'error.light' : 
                 actionConfig.buttonColor === 'warning' ? 'warning.light' : 'primary.light',
        color: 'white'
      }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            {actionConfig.buttonIcon}
            <Typography variant="h6" component="div">
              {actionConfig.title}
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={loading}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        {showConfirmation && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Account {actionConfig.status.toLowerCase()} successfully!
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Account Summary */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon color="primary" />
            Account Summary
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
                Company:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {account.company_name}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
                Contact:
              </Typography>
              <Typography variant="body2">
                {account.contact?.name}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
                Email:
              </Typography>
              <Typography variant="body2">
                {account.contact?.email}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
                Status:
              </Typography>
              <Chip 
                label={account.status_verbose || account.status} 
                color={actionConfig.buttonColor}
                size="small"
              />
            </Box>
          </Box>
        </Box>

        {/* Custom Data Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleIcon color="primary" />
            Custom Account Settings (Optional)
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel htmlFor="total-user-slots">Total User Slots</InputLabel>
                <OutlinedInput
                  id="total-user-slots"
                  type="number"
                  value={totalUserSlots}
                  onChange={(e) => setTotalUserSlots(e.target.value)}
                  disabled={loading}
                  startAdornment={
                    <InputAdornment position="start">
                      <PeopleIcon color="action" />
                    </InputAdornment>
                  }
                  label="Total User Slots"
                  inputProps={{
                    min: 0,
                    step: 1
                  }}
                />
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Expiry Date"
                  value={expiryDate}
                  onChange={(newValue) => setExpiryDate(newValue)}
                  disabled={loading}
                  minDate={dayjs()}
                  maxDate={dayjs().add(30, 'day')}
                  format="DD/MM/YY"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: "Maximum 30 days from today",
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <EventIcon color="action" />
                          </InputAdornment>
                        ),
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Box>

        {/* Remarks Section */}
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            Please add remarks to {actionConfig.buttonText}
          </Typography>
          
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            placeholder={`Enter detailed remarks for ${actionConfig.buttonText.toLowerCase()}ing this account. Minimum 10 characters required.`}
            value={remarks}
            onChange={handleRemarksChange}
            disabled={loading}
            error={!!error}
            helperText={
              error || 
              `${charCount}/${MAX_REMARKS_LENGTH} characters ${charCount < 10 ? '(minimum 10 required)' : ''}`
            }
            inputProps={{
              maxLength: MAX_REMARKS_LENGTH
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2, bgcolor: 'grey.50' }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          size="large"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color={actionConfig.buttonColor}
          startIcon={actionConfig.buttonIcon}
          disabled={loading || !remarks.trim() || remarks.trim().length < 10}
          size="large"
        >
          {loading ? (
            <>
              <Box sx={{ display: 'inline-block', animation: 'spin 1s linear infinite', mr: 1 }}>
                <CircularProgress size={16} color="inherit" />
              </Box>
              Processing...
            </>
          ) : (
            actionConfig.buttonText
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PendingAccountRemarks; 